/**
 * Centralized client-side logger that sanitizes error objects
 * to prevent leaking internal metadata (table names, schema info,
 * PostgREST codes, stack traces) in production console output.
 */

import * as Sentry from "@sentry/react";

const SENSITIVE_PATTERNS = [
  /PGRST\d+/i,
  /public\.\w+/i,
  /schema\s+cache/i,
  /auth\.users/i,
  /supabase/i,
  /postgresql/i,
  /pg_/i,
  /relation\s+"/i,
];

const SENSITIVE_KEYS = [
  'stack',
  'details',
  'hint',
  'schema',
  'table',
  'constraint',
  'routine',
  'file',
  'line',
  'internalQuery',
  'internalPosition',
  'where',
];

const isProd = () => import.meta.env.PROD;

/**
 * Extracts only safe fields from a Supabase/Postgres error object.
 * Never passes the raw object to console in production.
 */
function sanitizeError(error: unknown): Record<string, unknown> {
  if (!error || typeof error !== 'object') {
    return { message: String(error ?? 'Unknown error') };
  }

  const err = error as Record<string, unknown>;
  const safe: Record<string, unknown> = {};

  // Only keep message and code — sanitize their content too
  if (typeof err.message === 'string') {
    safe.message = isProd() ? scrubSensitive(err.message) : err.message;
  }
  if (typeof err.code === 'string') {
    safe.code = isProd() ? scrubSensitive(err.code) : err.code;
  }
  if (typeof err.status === 'number') {
    safe.status = err.status;
  }
  if (typeof err.statusCode === 'number') {
    safe.statusCode = err.statusCode;
  }

  return safe;
}

function scrubSensitive(text: string): string {
  let result = text;
  for (const pattern of SENSITIVE_PATTERNS) {
    result = result.replace(pattern, '[REDACTED]');
  }
  return result;
}

/**
 * Safe logger — use instead of console.error/console.log for error objects.
 *
 * Usage:
 *   import { logger } from '@/lib/logger';
 *   logger.error('Failed to fetch settings', error);
 *   logger.warn('Rate limit approaching', { remaining });
 *   logger.info('User logged in');
 */
export const logger = {
  error(context: string, error?: unknown) {
    // Send to Sentry regardless of environment
    if (error instanceof Error) {
      Sentry.captureException(error, { extra: { context } });
    } else {
      Sentry.captureMessage(`${context}: ${String(error ?? '')}`, { level: 'error' });
    }

    if (isProd()) {
      const safe = error ? sanitizeError(error) : {};
      console.error(`[Error] ${context}`, safe);
    } else {
      // In dev, show full error for debugging
      console.error(`[Error] ${context}`, error);
    }
  },

  warn(context: string, data?: unknown) {
    if (isProd()) {
      console.warn(`[Warn] ${context}`);
    } else {
      console.warn(`[Warn] ${context}`, data);
    }
  },

  info(context: string, data?: unknown) {
    if (isProd()) {
      // Suppress verbose info logs in production
      return;
    }
    console.log(`[Info] ${context}`, data);
  },

  debug(context: string, data?: unknown) {
    if (isProd()) return;
    console.log(`[Debug] ${context}`, data);
  },
};

export default logger;
